// NOSONAR
/* eslint-disable */
/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const N = globalThis, W = N.ShadowRoot && (N.ShadyCSS === void 0 || N.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, q = Symbol(), Y = /* @__PURE__ */ new WeakMap();
let ht = class {
  constructor(t, e, s) {
    if (this._$cssResult$ = !0, s !== q)
      throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = e;
  }
  get styleSheet() {
    let t = this.o;
    const e = this.t;
    if (W && t === void 0) {
      const s = e !== void 0 && e.length === 1;
      s && (t = Y.get(e)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), s && Y.set(e, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const yt = (o) => new ht(typeof o == "string" ? o : o + "", void 0, q), J = (o, ...t) => {
  const e = o.length === 1 ? o[0] : t.reduce((s, i, n) => s + ((r) => {
    if (r._$cssResult$ === !0)
      return r.cssText;
    if (typeof r == "number")
      return r;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + r + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(i) + o[n + 1], o[0]);
  return new ht(e, o, q);
}, _t = (o, t) => {
  if (W)
    o.adoptedStyleSheets = t.map((e) => e instanceof CSSStyleSheet ? e : e.styleSheet);
  else
    for (const e of t) {
      const s = document.createElement("style"), i = N.litNonce;
      i !== void 0 && s.setAttribute("nonce", i), s.textContent = e.cssText, o.appendChild(s);
    }
}, G = W ? (o) => o : (o) => o instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const s of t.cssRules)
    e += s.cssText;
  return yt(e);
})(o) : o;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: Lt, defineProperty: mt, getOwnPropertyDescriptor: Ct, getOwnPropertyNames: vt, getOwnPropertySymbols: bt, getPrototypeOf: At } = Object, m = globalThis, Q = m.trustedTypes, wt = Q ? Q.emptyScript : "", F = m.reactiveElementPolyfillSupport, k = (o, t) => o, R = { toAttribute(o, t) {
  switch (t) {
    case Boolean:
      o = o ? wt : null;
      break;
    case Object:
    case Array:
      o = o == null ? o : JSON.stringify(o);
  }
  return o;
}, fromAttribute(o, t) {
  let e = o;
  switch (t) {
    case Boolean:
      e = o !== null;
      break;
    case Number:
      e = o === null ? null : Number(o);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(o);
      } catch {
        e = null;
      }
  }
  return e;
} }, K = (o, t) => !Lt(o, t), X = { attribute: !0, type: String, converter: R, reflect: !1, hasChanged: K };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), m.litPropertyMetadata ?? (m.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
class x extends HTMLElement {
  static addInitializer(t) {
    this._$Ei(), (this.l ?? (this.l = [])).push(t);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(t, e = X) {
    if (e.state && (e.attribute = !1), this._$Ei(), this.elementProperties.set(t, e), !e.noAccessor) {
      const s = Symbol(), i = this.getPropertyDescriptor(t, s, e);
      i !== void 0 && mt(this.prototype, t, i);
    }
  }
  static getPropertyDescriptor(t, e, s) {
    const { get: i, set: n } = Ct(this.prototype, t) ?? { get() {
      return this[e];
    }, set(r) {
      this[e] = r;
    } };
    return { get() {
      return i == null ? void 0 : i.call(this);
    }, set(r) {
      const h = i == null ? void 0 : i.call(this);
      n.call(this, r), this.requestUpdate(t, h, s);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) ?? X;
  }
  static _$Ei() {
    if (this.hasOwnProperty(k("elementProperties")))
      return;
    const t = At(this);
    t.finalize(), t.l !== void 0 && (this.l = [...t.l]), this.elementProperties = new Map(t.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(k("finalized")))
      return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(k("properties"))) {
      const e = this.properties, s = [...vt(e), ...bt(e)];
      for (const i of s)
        this.createProperty(i, e[i]);
    }
    const t = this[Symbol.metadata];
    if (t !== null) {
      const e = litPropertyMetadata.get(t);
      if (e !== void 0)
        for (const [s, i] of e)
          this.elementProperties.set(s, i);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [e, s] of this.elementProperties) {
      const i = this._$Eu(e, s);
      i !== void 0 && this._$Eh.set(i, e);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(t) {
    const e = [];
    if (Array.isArray(t)) {
      const s = new Set(t.flat(1 / 0).reverse());
      for (const i of s)
        e.unshift(G(i));
    } else
      t !== void 0 && e.push(G(t));
    return e;
  }
  static _$Eu(t, e) {
    const s = e.attribute;
    return s === !1 ? void 0 : typeof s == "string" ? s : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var t;
    this._$Eg = new Promise((e) => this.enableUpdating = e), this._$AL = /* @__PURE__ */ new Map(), this._$ES(), this.requestUpdate(), (t = this.constructor.l) == null || t.forEach((e) => e(this));
  }
  addController(t) {
    var e;
    (this._$E_ ?? (this._$E_ = /* @__PURE__ */ new Set())).add(t), this.renderRoot !== void 0 && this.isConnected && ((e = t.hostConnected) == null || e.call(t));
  }
  removeController(t) {
    var e;
    (e = this._$E_) == null || e.delete(t);
  }
  _$ES() {
    const t = /* @__PURE__ */ new Map(), e = this.constructor.elementProperties;
    for (const s of e.keys())
      this.hasOwnProperty(s) && (t.set(s, this[s]), delete this[s]);
    t.size > 0 && (this._$Ep = t);
  }
  createRenderRoot() {
    const t = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return _t(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    var t;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (t = this._$E_) == null || t.forEach((e) => {
      var s;
      return (s = e.hostConnected) == null ? void 0 : s.call(e);
    });
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    var t;
    (t = this._$E_) == null || t.forEach((e) => {
      var s;
      return (s = e.hostDisconnected) == null ? void 0 : s.call(e);
    });
  }
  attributeChangedCallback(t, e, s) {
    this._$AK(t, s);
  }
  _$EO(t, e) {
    var n;
    const s = this.constructor.elementProperties.get(t), i = this.constructor._$Eu(t, s);
    if (i !== void 0 && s.reflect === !0) {
      const r = (((n = s.converter) == null ? void 0 : n.toAttribute) !== void 0 ? s.converter : R).toAttribute(e, s.type);
      this._$Em = t, r == null ? this.removeAttribute(i) : this.setAttribute(i, r), this._$Em = null;
    }
  }
  _$AK(t, e) {
    var n;
    const s = this.constructor, i = s._$Eh.get(t);
    if (i !== void 0 && this._$Em !== i) {
      const r = s.getPropertyOptions(i), h = typeof r.converter == "function" ? { fromAttribute: r.converter } : ((n = r.converter) == null ? void 0 : n.fromAttribute) !== void 0 ? r.converter : R;
      this._$Em = i, this[i] = h.fromAttribute(e, r.type), this._$Em = null;
    }
  }
  requestUpdate(t, e, s) {
    if (t !== void 0) {
      if (s ?? (s = this.constructor.getPropertyOptions(t)), !(s.hasChanged ?? K)(this[t], e))
        return;
      this.C(t, e, s);
    }
    this.isUpdatePending === !1 && (this._$Eg = this._$EP());
  }
  C(t, e, s) {
    this._$AL.has(t) || this._$AL.set(t, e), s.reflect === !0 && this._$Em !== t && (this._$ET ?? (this._$ET = /* @__PURE__ */ new Set())).add(t);
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$Eg;
    } catch (e) {
      Promise.reject(e);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var s;
    if (!this.isUpdatePending)
      return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [n, r] of this._$Ep)
          this[n] = r;
        this._$Ep = void 0;
      }
      const i = this.constructor.elementProperties;
      if (i.size > 0)
        for (const [n, r] of i)
          r.wrapped !== !0 || this._$AL.has(n) || this[n] === void 0 || this.C(n, this[n], r);
    }
    let t = !1;
    const e = this._$AL;
    try {
      t = this.shouldUpdate(e), t ? (this.willUpdate(e), (s = this._$E_) == null || s.forEach((i) => {
        var n;
        return (n = i.hostUpdate) == null ? void 0 : n.call(i);
      }), this.update(e)) : this._$Ej();
    } catch (i) {
      throw t = !1, this._$Ej(), i;
    }
    t && this._$AE(e);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    var e;
    (e = this._$E_) == null || e.forEach((s) => {
      var i;
      return (i = s.hostUpdated) == null ? void 0 : i.call(s);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$Ej() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$Eg;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$ET && (this._$ET = this._$ET.forEach((e) => this._$EO(e, this[e]))), this._$Ej();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
}
x.elementStyles = [], x.shadowRootOptions = { mode: "open" }, x[k("elementProperties")] = /* @__PURE__ */ new Map(), x[k("finalized")] = /* @__PURE__ */ new Map(), F == null || F({ ReactiveElement: x }), (m.reactiveElementVersions ?? (m.reactiveElementVersions = [])).push("2.0.3");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const P = globalThis, D = P.trustedTypes, tt = D ? D.createPolicy("lit-html", { createHTML: (o) => o }) : void 0, at = "$lit$", L = `lit$${(Math.random() + "").slice(9)}$`, ct = "?" + L, xt = `<${ct}>`, b = document, Z = () => b.createComment(""), T = (o) => o === null || typeof o != "object" && typeof o != "function", dt = Array.isArray, Mt = (o) => dt(o) || typeof (o == null ? void 0 : o[Symbol.iterator]) == "function", j = `[ 	
\f\r]`, S = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, et = /-->/g, st = />/g, C = RegExp(`>|${j}(?:([^\\s"'>=/]+)(${j}*=${j}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), it = /'/g, ot = /"/g, pt = /^(?:script|style|textarea|title)$/i, Et = (o) => (t, ...e) => ({ _$litType$: o, strings: t, values: e }), g = Et(1), A = Symbol.for("lit-noChange"), c = Symbol.for("lit-nothing"), rt = /* @__PURE__ */ new WeakMap(), v = b.createTreeWalker(b, 129);
function ut(o, t) {
  if (!Array.isArray(o) || !o.hasOwnProperty("raw"))
    throw Error("invalid template strings array");
  return tt !== void 0 ? tt.createHTML(t) : t;
}
const St = (o, t) => {
  const e = o.length - 1, s = [];
  let i, n = t === 2 ? "<svg>" : "", r = S;
  for (let h = 0; h < e; h++) {
    const l = o[h];
    let d, p, a = -1, y = 0;
    for (; y < l.length && (r.lastIndex = y, p = r.exec(l), p !== null); )
      y = r.lastIndex, r === S ? p[1] === "!--" ? r = et : p[1] !== void 0 ? r = st : p[2] !== void 0 ? (pt.test(p[2]) && (i = RegExp("</" + p[2], "g")), r = C) : p[3] !== void 0 && (r = C) : r === C ? p[0] === ">" ? (r = i ?? S, a = -1) : p[1] === void 0 ? a = -2 : (a = r.lastIndex - p[2].length, d = p[1], r = p[3] === void 0 ? C : p[3] === '"' ? ot : it) : r === ot || r === it ? r = C : r === et || r === st ? r = S : (r = C, i = void 0);
    const _ = r === C && o[h + 1].startsWith("/>") ? " " : "";
    n += r === S ? l + xt : a >= 0 ? (s.push(d), l.slice(0, a) + at + l.slice(a) + L + _) : l + L + (a === -2 ? h : _);
  }
  return [ut(o, n + (o[e] || "<?>") + (t === 2 ? "</svg>" : "")), s];
};
class O {
  constructor({ strings: t, _$litType$: e }, s) {
    let i;
    this.parts = [];
    let n = 0, r = 0;
    const h = t.length - 1, l = this.parts, [d, p] = St(t, e);
    if (this.el = O.createElement(d, s), v.currentNode = this.el.content, e === 2) {
      const a = this.el.content.firstChild;
      a.replaceWith(...a.childNodes);
    }
    for (; (i = v.nextNode()) !== null && l.length < h; ) {
      if (i.nodeType === 1) {
        if (i.hasAttributes())
          for (const a of i.getAttributeNames())
            if (a.endsWith(at)) {
              const y = p[r++], _ = i.getAttribute(a).split(L), B = /([.?@])?(.*)/.exec(y);
              l.push({ type: 1, index: n, name: B[2], strings: _, ctor: B[1] === "." ? Pt : B[1] === "?" ? Zt : B[1] === "@" ? Tt : z }), i.removeAttribute(a);
            } else
              a.startsWith(L) && (l.push({ type: 6, index: n }), i.removeAttribute(a));
        if (pt.test(i.tagName)) {
          const a = i.textContent.split(L), y = a.length - 1;
          if (y > 0) {
            i.textContent = D ? D.emptyScript : "";
            for (let _ = 0; _ < y; _++)
              i.append(a[_], Z()), v.nextNode(), l.push({ type: 2, index: ++n });
            i.append(a[y], Z());
          }
        }
      } else if (i.nodeType === 8)
        if (i.data === ct)
          l.push({ type: 2, index: n });
        else {
          let a = -1;
          for (; (a = i.data.indexOf(L, a + 1)) !== -1; )
            l.push({ type: 7, index: n }), a += L.length - 1;
        }
      n++;
    }
  }
  static createElement(t, e) {
    const s = b.createElement("template");
    return s.innerHTML = t, s;
  }
}
function E(o, t, e = o, s) {
  var r, h;
  if (t === A)
    return t;
  let i = s !== void 0 ? (r = e._$Co) == null ? void 0 : r[s] : e._$Cl;
  const n = T(t) ? void 0 : t._$litDirective$;
  return (i == null ? void 0 : i.constructor) !== n && ((h = i == null ? void 0 : i._$AO) == null || h.call(i, !1), n === void 0 ? i = void 0 : (i = new n(o), i._$AT(o, e, s)), s !== void 0 ? (e._$Co ?? (e._$Co = []))[s] = i : e._$Cl = i), i !== void 0 && (t = E(o, i._$AS(o, t.values), i, s)), t;
}
class kt {
  constructor(t, e) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = e;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    const { el: { content: e }, parts: s } = this._$AD, i = ((t == null ? void 0 : t.creationScope) ?? b).importNode(e, !0);
    v.currentNode = i;
    let n = v.nextNode(), r = 0, h = 0, l = s[0];
    for (; l !== void 0; ) {
      if (r === l.index) {
        let d;
        l.type === 2 ? d = new U(n, n.nextSibling, this, t) : l.type === 1 ? d = new l.ctor(n, l.name, l.strings, this, t) : l.type === 6 && (d = new Ot(n, this, t)), this._$AV.push(d), l = s[++h];
      }
      r !== (l == null ? void 0 : l.index) && (n = v.nextNode(), r++);
    }
    return v.currentNode = b, i;
  }
  p(t) {
    let e = 0;
    for (const s of this._$AV)
      s !== void 0 && (s.strings !== void 0 ? (s._$AI(t, s, e), e += s.strings.length - 2) : s._$AI(t[e])), e++;
  }
}
class U {
  get _$AU() {
    var t;
    return ((t = this._$AM) == null ? void 0 : t._$AU) ?? this._$Cv;
  }
  constructor(t, e, s, i) {
    this.type = 2, this._$AH = c, this._$AN = void 0, this._$AA = t, this._$AB = e, this._$AM = s, this.options = i, this._$Cv = (i == null ? void 0 : i.isConnected) ?? !0;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const e = this._$AM;
    return e !== void 0 && (t == null ? void 0 : t.nodeType) === 11 && (t = e.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, e = this) {
    t = E(this, t, e), T(t) ? t === c || t == null || t === "" ? (this._$AH !== c && this._$AR(), this._$AH = c) : t !== this._$AH && t !== A && this._(t) : t._$litType$ !== void 0 ? this.g(t) : t.nodeType !== void 0 ? this.$(t) : Mt(t) ? this.T(t) : this._(t);
  }
  k(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  $(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.k(t));
  }
  _(t) {
    this._$AH !== c && T(this._$AH) ? this._$AA.nextSibling.data = t : this.$(b.createTextNode(t)), this._$AH = t;
  }
  g(t) {
    var n;
    const { values: e, _$litType$: s } = t, i = typeof s == "number" ? this._$AC(t) : (s.el === void 0 && (s.el = O.createElement(ut(s.h, s.h[0]), this.options)), s);
    if (((n = this._$AH) == null ? void 0 : n._$AD) === i)
      this._$AH.p(e);
    else {
      const r = new kt(i, this), h = r.u(this.options);
      r.p(e), this.$(h), this._$AH = r;
    }
  }
  _$AC(t) {
    let e = rt.get(t.strings);
    return e === void 0 && rt.set(t.strings, e = new O(t)), e;
  }
  T(t) {
    dt(this._$AH) || (this._$AH = [], this._$AR());
    const e = this._$AH;
    let s, i = 0;
    for (const n of t)
      i === e.length ? e.push(s = new U(this.k(Z()), this.k(Z()), this, this.options)) : s = e[i], s._$AI(n), i++;
    i < e.length && (this._$AR(s && s._$AB.nextSibling, i), e.length = i);
  }
  _$AR(t = this._$AA.nextSibling, e) {
    var s;
    for ((s = this._$AP) == null ? void 0 : s.call(this, !1, !0, e); t && t !== this._$AB; ) {
      const i = t.nextSibling;
      t.remove(), t = i;
    }
  }
  setConnected(t) {
    var e;
    this._$AM === void 0 && (this._$Cv = t, (e = this._$AP) == null || e.call(this, t));
  }
}
class z {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(t, e, s, i, n) {
    this.type = 1, this._$AH = c, this._$AN = void 0, this.element = t, this.name = e, this._$AM = i, this.options = n, s.length > 2 || s[0] !== "" || s[1] !== "" ? (this._$AH = Array(s.length - 1).fill(new String()), this.strings = s) : this._$AH = c;
  }
  _$AI(t, e = this, s, i) {
    const n = this.strings;
    let r = !1;
    if (n === void 0)
      t = E(this, t, e, 0), r = !T(t) || t !== this._$AH && t !== A, r && (this._$AH = t);
    else {
      const h = t;
      let l, d;
      for (t = n[0], l = 0; l < n.length - 1; l++)
        d = E(this, h[s + l], e, l), d === A && (d = this._$AH[l]), r || (r = !T(d) || d !== this._$AH[l]), d === c ? t = c : t !== c && (t += (d ?? "") + n[l + 1]), this._$AH[l] = d;
    }
    r && !i && this.O(t);
  }
  O(t) {
    t === c ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class Pt extends z {
  constructor() {
    super(...arguments), this.type = 3;
  }
  O(t) {
    this.element[this.name] = t === c ? void 0 : t;
  }
}
class Zt extends z {
  constructor() {
    super(...arguments), this.type = 4;
  }
  O(t) {
    this.element.toggleAttribute(this.name, !!t && t !== c);
  }
}
class Tt extends z {
  constructor(t, e, s, i, n) {
    super(t, e, s, i, n), this.type = 5;
  }
  _$AI(t, e = this) {
    if ((t = E(this, t, e, 0) ?? c) === A)
      return;
    const s = this._$AH, i = t === c && s !== c || t.capture !== s.capture || t.once !== s.once || t.passive !== s.passive, n = t !== c && (s === c || i);
    i && this.element.removeEventListener(this.name, this, s), n && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    var e;
    typeof this._$AH == "function" ? this._$AH.call(((e = this.options) == null ? void 0 : e.host) ?? this.element, t) : this._$AH.handleEvent(t);
  }
}
class Ot {
  constructor(t, e, s) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = e, this.options = s;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    E(this, t);
  }
}
const I = P.litHtmlPolyfillSupport;
I == null || I(O, U), (P.litHtmlVersions ?? (P.litHtmlVersions = [])).push("3.1.1");
const Ut = (o, t, e) => {
  const s = (e == null ? void 0 : e.renderBefore) ?? t;
  let i = s._$litPart$;
  if (i === void 0) {
    const n = (e == null ? void 0 : e.renderBefore) ?? null;
    s._$litPart$ = i = new U(t.insertBefore(Z(), n), n, void 0, e ?? {});
  }
  return i._$AI(o), i;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
class M extends x {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var e;
    const t = super.createRenderRoot();
    return (e = this.renderOptions).renderBefore ?? (e.renderBefore = t.firstChild), t;
  }
  update(t) {
    const e = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = Ut(e, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var t;
    super.connectedCallback(), (t = this._$Do) == null || t.setConnected(!0);
  }
  disconnectedCallback() {
    var t;
    super.disconnectedCallback(), (t = this._$Do) == null || t.setConnected(!1);
  }
  render() {
    return A;
  }
}
var lt;
M._$litElement$ = !0, M.finalized = !0, (lt = globalThis.litElementHydrateSupport) == null || lt.call(globalThis, { LitElement: M });
const V = globalThis.litElementPolyfillSupport;
V == null || V({ LitElement: M });
(globalThis.litElementVersions ?? (globalThis.litElementVersions = [])).push("4.0.3");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const $t = (o) => (t, e) => {
  e !== void 0 ? e.addInitializer(() => {
    customElements.define(o, t);
  }) : customElements.define(o, t);
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ht = { attribute: !0, type: String, converter: R, reflect: !1, hasChanged: K }, Bt = (o = Ht, t, e) => {
  const { kind: s, metadata: i } = e;
  let n = globalThis.litPropertyMetadata.get(i);
  if (n === void 0 && globalThis.litPropertyMetadata.set(i, n = /* @__PURE__ */ new Map()), n.set(e.name, o), s === "accessor") {
    const { name: r } = e;
    return { set(h) {
      const l = t.get.call(this);
      t.set.call(this, h), this.requestUpdate(r, l, o);
    }, init(h) {
      return h !== void 0 && this.C(r, void 0, o), h;
    } };
  }
  if (s === "setter") {
    const { name: r } = e;
    return function(h) {
      const l = this[r];
      t.call(this, h), this.requestUpdate(r, l, o);
    };
  }
  throw Error("Unsupported decorator location: " + s);
};
function u(o) {
  return (t, e) => typeof e == "object" ? Bt(o, t, e) : ((s, i, n) => {
    const r = i.hasOwnProperty(n);
    return i.constructor.createProperty(n, r ? { ...s, wrapped: !0 } : s), r ? Object.getOwnPropertyDescriptor(i, n) : void 0;
  })(o, t, e);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Nt = { ATTRIBUTE: 1, CHILD: 2, PROPERTY: 3, BOOLEAN_ATTRIBUTE: 4, EVENT: 5, ELEMENT: 6 }, Rt = (o) => (...t) => ({ _$litDirective$: o, values: t });
let Dt = class {
  constructor(t) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(t, e, s) {
    this._$Ct = t, this._$AM = e, this._$Ci = s;
  }
  _$AS(t, e) {
    return this.update(t, e);
  }
  update(t, e) {
    return this.render(...e);
  }
};
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ft = "important", zt = " !" + ft, Ft = Rt(class extends Dt {
  constructor(o) {
    var t;
    if (super(o), o.type !== Nt.ATTRIBUTE || o.name !== "style" || ((t = o.strings) == null ? void 0 : t.length) > 2)
      throw Error("The `styleMap` directive must be used in the `style` attribute and must be the only part in the attribute.");
  }
  render(o) {
    return Object.keys(o).reduce((t, e) => {
      const s = o[e];
      return s == null ? t : t + `${e = e.includes("-") ? e : e.replace(/(?:^(webkit|moz|ms|o)|)(?=[A-Z])/g, "-$&").toLowerCase()}:${s};`;
    }, "");
  }
  update(o, [t]) {
    const { style: e } = o.element;
    if (this.ut === void 0)
      return this.ut = new Set(Object.keys(t)), this.render(t);
    for (const s of this.ut)
      t[s] == null && (this.ut.delete(s), s.includes("-") ? e.removeProperty(s) : e[s] = null);
    for (const s in t) {
      const i = t[s];
      if (i != null) {
        this.ut.add(s);
        const n = typeof i == "string" && i.endsWith(zt);
        s.includes("-") || n ? e.setProperty(s, n ? i.slice(0, -11) : i, n ? ft : "") : e[s] = i;
      }
    }
    return A;
  }
}), gt = J`
    * {
        min-width: 0;
        font: inherit;
    }

    *, *::before, *::after {
        box-sizing: border-box;
    }

    img, video, svg {
        display: block;
        height: auto;
        max-width: 100%;
    }

    p, h1, h2, h3, h4, h5, h6{
        margin: 0;
    }
`, jt = J`
.storybook-button {
  font-family: 'Nunito Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 700;
  border: 0;
  border-radius: 3em;
  cursor: pointer;
  display: inline-block;
  line-height: 1;
  
}
.storybook-button--primary {
  color: var( --color-button ), white;
  background-color:  #1ea7fd;
}
.storybook-button--secondary {
  color: #333;
  background-color: transparent;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 0px 0px 1px inset;
}
.storybook-button--small {
  font-size: 12px;
  padding: 10px 16px;
}
.storybook-button--medium {
  font-size: 14px;
  padding: 11px 20px;
}
.storybook-button--large {
  font-size: 16px;
  padding: 12px 24px;
}
`;
var It = Object.defineProperty, Vt = Object.getOwnPropertyDescriptor, H = (o, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? Vt(t, e) : t, n = o.length - 1, r; n >= 0; n--)
    (r = o[n]) && (i = (s ? r(t, e, i) : r(i)) || i);
  return s && i && It(t, e, i), i;
};
let w = class extends M {
  constructor() {
    super(...arguments), this.message = "My button", this.primary = !1, this.backgroundColor = "transparent", this.size = "medium";
  }
  render() {
    const o = this.primary ? "storybook-button--primary" : "storybook-button--secondary";
    return g`
      <button
        type="button"
        class=${["storybook-button", `storybook-button--${this.size}`, o].join(" ")}
        style=${Ft({ backgroundColor: this.backgroundColor })}
      >
        ${this.message}
      </button>
    `;
  }
};
w.styles = [gt, jt];
H([
  u()
], w.prototype, "message", 2);
H([
  u({ reflect: !0 })
], w.prototype, "primary", 2);
H([
  u()
], w.prototype, "backgroundColor", 2);
H([
  u()
], w.prototype, "size", 2);
w = H([
  $t("my-button")
], w);
const Wt = J`

:host{
  background-color: #FFFFFF;
  border-radius: 5px;
  position: relative;
}
.notification{
  box-shadow: 0px 2px 4px 0px rgba(6, 21, 86, 0.15);
  line-height: 150%;
  font-size: inherit;
 @media screen and (width >= 768px) {
  border-radius: 5px;
  display: flex;

  .content{
    padding: 20px;
    border-radius: 0px 5px 5px 0px;
  }

  .content-title{
    display: block;
  }

  .head-title, .close-icon{
    display: none;
  }

  .content .close-icon{
    display: block;
    position: absolute;
    right: 5px;
    top: 10px;
    color: var(--primary-color, #005EDC);
  }

  .head{
    border-radius: 5px 0px 0px 5px;
    width: 60px;
    min-width: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .condensed{
    display: block;
  }
 }
}

.notification.closed {
  display: none;
}

.close-icon {
  display: block;
  border: none;
  background-color: transparent;
  color: inherit;
  cursor: pointer;
}

.head{
  display: flex;
  padding: 5px 15px;
  color: var(--white-color, #FFFFFF)
}



.head--success{
  background-color: var(--success-color, #05A56B);
}

.head--error{
  background-color: var(--error-color, #ED1D24);
}

.head--warning{
  background-color: var(--warning-color, #FDBB11);
  color: var(--primary-text-color, #4A4A4A);
}

.head--info{
  background-color: var(--offers-color, #00A0CC);
}

.head--offers{
  background-color: var(--offers-color, #BD10E0);
}

.head-title{
  flex: auto;
  text-align: left;
  padding-left: 5px; 
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
}
.content{
  padding: 20px 16px;
  text-align: left;
  background-color: #FFFFFF;
  width: 100%;
  border-radius: 0px;
 

  .close-icon{
    display: none;
  }

  .content-title{
    font-weight: 600;
    padding-bottom: 8px;
  }
}

.content-title{
  display: none;
}

.condensed{
  display: none;
}

a{
  margin-left: 8px;
  color: var(--primary-color, #005EDC);
  text-decoration: underline;
}
`;
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const nt = (o) => o ?? c;
var qt = Object.defineProperty, Jt = Object.getOwnPropertyDescriptor, f = (o, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? Jt(t, e) : t, n = o.length - 1, r; n >= 0; n--)
    (r = o[n]) && (i = (s ? r(t, e, i) : r(i)) || i);
  return s && i && qt(t, e, i), i;
};
let $ = class extends M {
  constructor() {
    super(...arguments), this.type = "success", this.title = "Default title", this.description = "Default description", this.renderCloseBtn = !1, this.isBannerOpen = !0, this.size = "medium";
  }
  getIconType() {
    if (this.type)
      switch (this.type) {
        case "success":
          return g`
          <svg class='main-icon' xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
            <path d="M12.5 21.775L6.25 15.525L8.0125 13.7625L12.5 18.2375L21.9875 8.75L23.75 10.525L12.5 21.775Z" fill="currentColor"/>
          </svg>
        `;
        case "error":
          return g`
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 22 20" fill="none">
          <path d="M20.4112 16.1533L12.2872 2.08182C11.9898 1.56704 11.4406 1.25 10.8462 1.25C10.2518 1.25 9.70259 1.56704 9.40527 2.08182L1.22281 16.2545C0.925731 16.7693 0.925731 17.4034 1.22281 17.9182C1.52012 18.433 2.06936 18.75 2.66375 18.75H19.0289C19.0306 18.75 19.0325 18.75 19.0337 18.75C19.9525 18.75 20.6973 18.0052 20.6973 17.0864C20.6971 16.7406 20.5918 16.4192 20.4112 16.1533Z" stroke="currentColor"/>
          <path d="M10.8486 6.14453C11.3619 6.14453 11.7783 6.56068 11.7783 7.07426L11.5406 12.6192C11.5169 13.1206 11.1271 13.3112 10.8486 13.3112C10.5365 13.3112 10.1838 13.1468 10.1565 12.6192L9.91882 7.07426C9.91882 6.56068 10.3352 6.14453 10.8486 6.14453Z" fill="currentColor"/>
          <path d="M11.6875 16.1222C11.4686 16.3432 11.1621 16.4691 10.8486 16.4691C10.5349 16.4691 10.2283 16.3432 10.0073 16.1222C9.78624 15.9011 9.66028 15.5946 9.66028 15.2808C9.66028 14.9671 9.78624 14.6629 10.0073 14.4419C10.2283 14.2209 10.5349 14.0925 10.8486 14.0925C11.1621 14.0925 11.4686 14.2209 11.6875 14.4419C11.9109 14.6629 12.0369 14.9695 12.0369 15.2808C12.0369 15.5946 11.9109 15.9011 11.6875 16.1222Z" fill="currentColor"/>
        </svg>
        `;
        case "warning":
          return g`
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="9.5" stroke="currentColor"/>
          <g transform="translate(10, 10) scale(0.8)">
          <path d="M-6.25 -6.25L6.25 6.25" stroke="currentColor" stroke-width="1.66667"/>
          <path d="M6.25 -6.25L-6.25 6.25" stroke="currentColor" stroke-width="1.66667"/>
          </g>
        </svg>
        `;
        case "info":
          return g`
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
          <path d="M5.5 15C5.5 9.75614 9.75614 5.5 15 5.5C20.2439 5.5 24.5 9.75614 24.5 15C24.5 20.2439 20.2439 24.5 15 24.5C9.75614 24.5 5.5 20.2439 5.5 15Z" stroke="currentColor"/>
          <path d="M16.0001 20H14.0001V14H16.0001V20Z" fill="currentColor"/>
          <path d="M16.0001 12H14.0001V10H16.0001V12Z" fill="currentColor"/>
        </svg>
        `;
        case "offers":
          return g`
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
          <mask id="path-1-outside-1_1312_14641" maskUnits="userSpaceOnUse" x="4" y="4" width="22" height="22" fill="black">
            <rect fill="white" x="4" y="4" width="22" height="22"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.7581 17.3682L5.00022 16.6314L16.5263 5L22.8946 5.56841V6.6105L16.9369 6.05261L5.7581 17.3682ZM24.4 8.7263L17.5686 8.15789L9.03186 16.7472C8.03445 17.7727 8.03445 19.4058 9.03186 20.4314L12.8002 24.2313C13.2849 24.7231 13.9465 25 14.637 25C15.3275 25 15.9892 24.7231 16.4738 24.2313L25 15.6209L24.4 8.7263ZM13.5583 23.5366L9.78996 19.7367C9.48779 19.4398 9.31759 19.034 9.31759 18.6104C9.31759 18.1868 9.48779 17.781 9.78996 17.4841L17.9688 9.21056L23.4213 9.69477L23.9476 15.221L15.6635 23.5366C15.068 24.0825 14.1539 24.0825 13.5583 23.5366ZM19.6004 12.1051C19.2061 12.5131 19.2061 13.1603 19.6004 13.5682C20.007 13.9559 20.6464 13.9559 21.053 13.5682C21.4474 13.1603 21.4474 12.5131 21.053 12.1051C20.6464 11.7175 20.007 11.7175 19.6004 12.1051Z"/>
          </mask>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.7581 17.3682L5.00022 16.6314L16.5263 5L22.8946 5.56841V6.6105L16.9369 6.05261L5.7581 17.3682ZM24.4 8.7263L17.5686 8.15789L9.03186 16.7472C8.03445 17.7727 8.03445 19.4058 9.03186 20.4314L12.8002 24.2313C13.2849 24.7231 13.9465 25 14.637 25C15.3275 25 15.9892 24.7231 16.4738 24.2313L25 15.6209L24.4 8.7263ZM13.5583 23.5366L9.78996 19.7367C9.48779 19.4398 9.31759 19.034 9.31759 18.6104C9.31759 18.1868 9.48779 17.781 9.78996 17.4841L17.9688 9.21056L23.4213 9.69477L23.9476 15.221L15.6635 23.5366C15.068 24.0825 14.1539 24.0825 13.5583 23.5366ZM19.6004 12.1051C19.2061 12.5131 19.2061 13.1603 19.6004 13.5682C20.007 13.9559 20.6464 13.9559 21.053 13.5682C21.4474 13.1603 21.4474 12.5131 21.053 12.1051C20.6464 11.7175 20.007 11.7175 19.6004 12.1051Z" fill="currentColor"/>
          <path d="M5.00022 16.6314L4.82595 16.8106C4.77812 16.7641 4.75085 16.7004 4.75023 16.6337C4.74961 16.567 4.77568 16.5028 4.82264 16.4554L5.00022 16.6314ZM5.7581 17.3682L5.93595 17.5439C5.83946 17.6416 5.68227 17.6432 5.58383 17.5475L5.7581 17.3682ZM16.5263 5L16.3488 4.82403C16.4012 4.77115 16.4744 4.74437 16.5486 4.75099L16.5263 5ZM22.8946 5.56841L22.9169 5.3194C23.0458 5.33091 23.1446 5.43895 23.1446 5.56841H22.8946ZM22.8946 6.6105H23.1446C23.1446 6.68086 23.115 6.74796 23.063 6.79533C23.011 6.84271 22.9414 6.86597 22.8713 6.85941L22.8946 6.6105ZM16.9369 6.05261L16.759 5.87691C16.8117 5.82359 16.8855 5.79671 16.9602 5.8037L16.9369 6.05261ZM17.5686 8.15789L17.3912 7.98165C17.4433 7.92931 17.5157 7.90263 17.5893 7.90875L17.5686 8.15789ZM24.4 8.7263L24.4207 8.47716C24.5421 8.48726 24.6385 8.58331 24.6491 8.70462L24.4 8.7263ZM9.03186 16.7472L8.85263 16.5729L8.85454 16.571L9.03186 16.7472ZM9.03186 20.4314L8.85434 20.6074L8.85264 20.6057L9.03186 20.4314ZM12.8002 24.2313L12.9777 24.0553L12.9783 24.0558L12.8002 24.2313ZM16.4738 24.2313L16.2958 24.0558L16.2962 24.0554L16.4738 24.2313ZM25 15.6209L25.2491 15.5992C25.2554 15.6724 25.2293 15.7446 25.1776 15.7968L25 15.6209ZM9.78996 19.7367L9.96518 19.5583L9.96747 19.5607L9.78996 19.7367ZM13.5583 23.5366L13.3892 23.7211L13.3808 23.7127L13.5583 23.5366ZM9.78996 17.4841L9.96777 17.6599L9.96517 17.6624L9.78996 17.4841ZM17.9688 9.21056L17.791 9.03481C17.8434 8.98181 17.9167 8.95495 17.9909 8.96154L17.9688 9.21056ZM23.4213 9.69477L23.4434 9.44575C23.5634 9.4564 23.6588 9.55113 23.6702 9.67106L23.4213 9.69477ZM23.9476 15.221L24.1965 15.1973C24.2035 15.2714 24.1772 15.3447 24.1247 15.3974L23.9476 15.221ZM15.6635 23.5366L15.8408 23.7133L15.8325 23.7209L15.6635 23.5366ZM19.6004 13.5682L19.4278 13.7494L19.4207 13.742L19.6004 13.5682ZM19.6004 12.1051L19.4205 11.9312L19.4279 11.9242L19.6004 12.1051ZM21.053 13.5682L21.2329 13.7421L21.2255 13.7492L21.053 13.5682ZM21.053 12.1051L21.2257 11.924L21.2328 11.9314L21.053 12.1051ZM5.17449 16.4521L5.93237 17.189L5.58383 17.5475L4.82595 16.8106L5.17449 16.4521ZM16.7039 5.17597L5.1778 16.8073L4.82264 16.4554L16.3488 4.82403L16.7039 5.17597ZM22.8724 5.81742L16.5041 5.24901L16.5486 4.75099L22.9169 5.3194L22.8724 5.81742ZM22.6446 6.6105V5.56841H23.1446V6.6105H22.6446ZM16.9602 5.8037L22.918 6.36159L22.8713 6.85941L16.9135 6.30152L16.9602 5.8037ZM5.58025 17.1925L16.759 5.87691L17.1147 6.22831L5.93595 17.5439L5.58025 17.1925ZM17.5893 7.90875L24.4207 8.47716L24.3793 8.97544L17.5478 8.40703L17.5893 7.90875ZM8.85454 16.571L17.3912 7.98165L17.7459 8.33412L9.20918 16.9234L8.85454 16.571ZM8.85264 20.6057C7.76085 19.4831 7.76085 17.6955 8.85264 16.5729L9.21107 16.9215C8.30804 17.85 8.30804 19.3286 9.21107 20.2571L8.85264 20.6057ZM12.6227 24.4073L8.85434 20.6074L9.20937 20.2553L12.9777 24.0553L12.6227 24.4073ZM14.637 25.25C13.8796 25.25 13.1538 24.9462 12.6222 24.4068L12.9783 24.0558C13.416 24.4999 14.0135 24.75 14.637 24.75V25.25ZM16.6519 24.4068C16.1202 24.9462 15.3944 25.25 14.637 25.25V24.75C15.2606 24.75 15.8581 24.4999 16.2958 24.0558L16.6519 24.4068ZM25.1776 15.7968L16.6515 24.4072L16.2962 24.0554L24.8224 15.445L25.1776 15.7968ZM24.6491 8.70462L25.2491 15.5992L24.7509 15.6426L24.151 8.74797L24.6491 8.70462ZM9.96747 19.5607L13.7358 23.3606L13.3808 23.7127L9.61245 19.9127L9.96747 19.5607ZM9.56759 18.6104C9.56759 18.9669 9.71084 19.3085 9.96517 19.5584L9.61475 19.915C9.26474 19.5712 9.06759 19.1011 9.06759 18.6104H9.56759ZM9.96517 17.6624C9.71084 17.9123 9.56759 18.2539 9.56759 18.6104H9.06759C9.06759 18.1197 9.26474 17.6496 9.61475 17.3058L9.96517 17.6624ZM18.1466 9.38632L9.96775 17.6599L9.61217 17.3083L17.791 9.03481L18.1466 9.38632ZM23.3992 9.94379L17.9467 9.45958L17.9909 8.96154L23.4434 9.44575L23.3992 9.94379ZM23.6987 15.2447L23.1724 9.71847L23.6702 9.67106L24.1965 15.1973L23.6987 15.2447ZM15.4864 23.3602L23.7705 15.0445L24.1247 15.3974L15.8407 23.7131L15.4864 23.3602ZM13.7272 23.3523C14.2272 23.8106 14.9946 23.8106 15.4946 23.3523L15.8325 23.7209C15.1413 24.3544 14.0806 24.3544 13.3894 23.7209L13.7272 23.3523ZM19.4207 13.742C18.9326 13.2371 18.9326 12.4363 19.4207 11.9314L19.7802 12.2789C19.4795 12.59 19.4795 13.0834 19.7802 13.3945L19.4207 13.742ZM21.2255 13.7492C20.7223 14.2289 19.9311 14.2289 19.4279 13.7492L19.7729 13.3873C20.083 13.6829 20.5705 13.6829 20.8805 13.3873L21.2255 13.7492ZM21.2328 11.9314C21.7208 12.4363 21.7208 13.2371 21.2328 13.742L20.8733 13.3945C21.174 13.0834 21.174 12.59 20.8733 12.2789L21.2328 11.9314ZM19.4279 11.9242C19.9311 11.4445 20.7223 11.4445 21.2255 11.9242L20.8805 12.2861C20.5705 11.9905 20.083 11.9905 19.7729 12.2861L19.4279 11.9242Z" fill="currentColor" mask="url(#path-1-outside-1_1312_14641)"/>
        </svg>
        `;
        default:
          return;
      }
  }
  getCloseButton() {
    return this.renderCloseBtn ? g`
      <button class='close-icon' @click='${this.handleClick}'>
        <svg  xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
          <path d="M2.5 3L12.5 13" stroke="currentColor" stroke-width="1.66667"/>
          <path d="M12.5 3L2.5 13" stroke="currentColor" stroke-width="1.66667"/>
        </svg>
      </button>
      ` : null;
  }
  renderAnchor() {
    return g`<a href=${nt(this.linkDestination)} target=${nt(this.linkTarget)}>${this.linkLabel}</a>`;
  }
  handleClick() {
    this.closeNotification(), this.dispatchEvent(new Event("close", { bubbles: !0, composed: !0 }));
  }
  checkForSlot() {
    return this.useSlot ? g`<slot></slot>` : g`<p part="content"> ${this.description}${this.linkLabel && this.renderAnchor()}</p> `;
  }
  render() {
    return this.isBannerOpen ? g`
       <div class='notification' exportParts="headTitle contentTitle content">
          <div class='head head--${this.type}' >
           ${this.getIconType()}
            <h3 class='head-title' part="headTitle">${this.title}</h3>
            ${this.getCloseButton()}
          </div>
         
          <div class='content ${this.condensed ? "condensed" : ""}'>
            ${this.getCloseButton()}
            <h3 class='content-title' part="contentTitle">${this.title}</h3>
            ${this.checkForSlot()}
          </div>
       </div>
      ` : null;
  }
  closeNotification() {
    this.isBannerOpen = !1;
  }
};
$.styles = [gt, Wt];
f([
  u()
], $.prototype, "type", 2);
f([
  u()
], $.prototype, "title", 2);
f([
  u()
], $.prototype, "description", 2);
f([
  u()
], $.prototype, "linkLabel", 2);
f([
  u()
], $.prototype, "linkDestination", 2);
f([
  u()
], $.prototype, "linkTarget", 2);
f([
  u()
], $.prototype, "renderCloseBtn", 2);
f([
  u()
], $.prototype, "condensed", 2);
f([
  u()
], $.prototype, "isBannerOpen", 2);
f([
  u()
], $.prototype, "useSlot", 2);
f([
  u()
], $.prototype, "size", 2);
$ = f([
  $t("notification-banner")
], $);
export {
  w as MyButton,
  $ as NotificationBanner
};
